import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Globe2,
  ShieldAlert,
  Bot,
  User as UserIcon,
  Monitor,
  Smartphone,
  Search,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Lock,
} from 'lucide-react';
import { BACKEND_URL } from '../lib/api';

const API = BACKEND_URL;
const TOKEN_KEY = 'ip_logs_token';

/**
 * IpLogs, private visitor analytics dashboard.
 * • Password-gated against `/api/marketing/ip-logs/auth`
 * • Excluded from search engines via robots.txt and meta noindex
 * • All filters are server-evaluated (Mongo query + paging)
 */
export default function IpLogs() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');

  // Block bots / crawlers via meta tags on this page only
  useEffect(() => {
    document.title = 'Restricted · Visitor Logs';
    const tags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex' },
      { name: 'googlebot', content: 'noindex, nofollow' },
      { name: 'bingbot', content: 'noindex, nofollow' },
    ];
    const created = tags.map((t) => {
      const el = document.createElement('meta');
      el.name = t.name;
      el.content = t.content;
      el.setAttribute('data-iplogs', '1');
      document.head.appendChild(el);
      return el;
    });
    return () => created.forEach((el) => el.remove());
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
  };

  if (!token) return <PasswordGate onAuthed={(t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}

// ─── Password gate ──────────────────────────────────────────────────
function PasswordGate({ onAuthed }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/marketing/ip-logs/auth`, { password });
      if (res.data?.token) {
        onAuthed(res.data.token);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: 'radial-gradient(circle at 30% 20%, #1c1f2c 0%, #0a0b10 60%)' }}
      data-testid="ip-logs-gate"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-xl p-7 shadow-2xl"
      >
        <div className="flex items-center gap-2 mb-6">
          <Lock className="w-5 h-5 text-amber-300" />
          <span className="text-zinc-100 font-semibold tracking-tight">Restricted area</span>
        </div>
        <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
          This dashboard is private. Enter the access password to continue.
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Access password"
          autoFocus
          autoComplete="off"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-amber-300/50"
          data-testid="ip-logs-password-input"
        />
        {error && (
          <p className="mt-3 text-sm text-rose-400" data-testid="ip-logs-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-5 w-full bg-amber-300 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 font-semibold text-sm rounded-md py-2.5 transition-colors"
          data-testid="ip-logs-submit"
        >
          {loading ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────
const PAGE_SIZE = 50;

function Dashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, bots: 0, humans: 0, unique_ips: 0, unique_countries: 0 });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [bot, setBot] = useState(''); // '', 'true', 'false'
  const [country, setCountry] = useState('');
  const [browser, setBrowser] = useState('');
  const [device, setDevice] = useState('');
  const [pathFilter, setPathFilter] = useState('');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      if (q) params.set('q', q);
      if (bot) params.set('bot', bot);
      if (country) params.set('country', country);
      if (browser) params.set('browser', browser);
      if (device) params.set('device', device);
      if (pathFilter) params.set('path', pathFilter);

      const res = await axios.get(`${API}/api/marketing/ip-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      setItems(res.data?.items || []);
      setStats(res.data?.stats || stats);
      setTotal(res.data?.total || 0);
      setPages(res.data?.pages || 1);
    } catch (err) {
      if (axios.isCancel?.(err)) return;
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err?.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, bot, country, browser, device, pathFilter, token]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const resetFilters = () => {
    setQ('');
    setBot('');
    setCountry('');
    setBrowser('');
    setDevice('');
    setPathFilter('');
    setPage(1);
  };

  const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg px-4 py-3 flex items-center gap-3">
      <Icon className={`w-5 h-5 ${accent}`} />
      <div>
        <div className="text-xl font-semibold text-zinc-100 tabular-nums">{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="ip-logs-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-amber-300" />
            <span className="font-semibold tracking-tight text-sm">Visitor logs</span>
            <span className="text-xs text-zinc-500 ml-2">private · noindex</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="text-xs px-3 py-1.5 rounded-md border border-zinc-800 hover:bg-zinc-900 inline-flex items-center gap-1.5"
              data-testid="ip-logs-refresh"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={onLogout}
              className="text-xs px-3 py-1.5 rounded-md border border-zinc-800 hover:bg-zinc-900 inline-flex items-center gap-1.5"
              data-testid="ip-logs-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Globe2} label="Total visits" value={stats.total} accent="text-zinc-400" />
          <StatCard icon={UserIcon} label="Humans" value={stats.humans} accent="text-emerald-400" />
          <StatCard icon={Bot} label="Bots" value={stats.bots} accent="text-rose-400" />
          <StatCard icon={ShieldAlert} label="Unique IPs" value={stats.unique_ips} accent="text-sky-400" />
          <StatCard icon={Globe2} label="Countries" value={stats.unique_countries} accent="text-amber-300" />
        </div>

        {/* Filters */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-3">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Search</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => { setPage(1); setQ(e.target.value); }}
                placeholder="IP / city / UA"
                className="w-full pl-8 bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:border-amber-300/50"
                data-testid="filter-q"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Path contains</label>
            <input
              value={pathFilter}
              onChange={(e) => { setPage(1); setPathFilter(e.target.value); }}
              placeholder="/site/pricing"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:border-amber-300/50"
              data-testid="filter-path"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Country</label>
            <input
              value={country}
              onChange={(e) => { setPage(1); setCountry(e.target.value); }}
              placeholder="Ireland / IE"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:border-amber-300/50"
              data-testid="filter-country"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Browser</label>
            <select
              value={browser}
              onChange={(e) => { setPage(1); setBrowser(e.target.value); }}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-amber-300/50"
              data-testid="filter-browser"
            >
              <option value="">Any</option>
              <option>Chrome</option>
              <option>Safari</option>
              <option>Firefox</option>
              <option>Edge</option>
              <option>Opera</option>
              <option>IE</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Device</label>
            <select
              value={device}
              onChange={(e) => { setPage(1); setDevice(e.target.value); }}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-amber-300/50"
              data-testid="filter-device"
            >
              <option value="">Any</option>
              <option>Desktop</option>
              <option>Mobile</option>
              <option>Tablet</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Type</label>
            <select
              value={bot}
              onChange={(e) => { setPage(1); setBot(e.target.value); }}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-amber-300/50"
              data-testid="filter-bot"
            >
              <option value="">All</option>
              <option value="false">Human</option>
              <option value="true">Bot</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <button
              onClick={resetFilters}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium rounded-md px-3 py-2"
              data-testid="filter-reset"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-900 text-zinc-400 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2.5">When</th>
                  <th className="text-left px-3 py-2.5">IP</th>
                  <th className="text-left px-3 py-2.5">Country</th>
                  <th className="text-left px-3 py-2.5">Path</th>
                  <th className="text-left px-3 py-2.5">Browser</th>
                  <th className="text-left px-3 py-2.5">OS</th>
                  <th className="text-left px-3 py-2.5">Device</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && (
                  <tr><td colSpan={8} className="text-center px-3 py-12 text-zinc-500">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && !error && (
                  <tr><td colSpan={8} className="text-center px-3 py-12 text-zinc-500">No visits match the current filters.</td></tr>
                )}
                {error && (
                  <tr><td colSpan={8} className="text-center px-3 py-12 text-rose-400">{error}</td></tr>
                )}
                {items.map((row) => (
                  <Row key={row.visit_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 text-xs text-zinc-400">
            <div>
              {total > 0
                ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`
                : ','}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="p-1.5 rounded border border-zinc-800 hover:bg-zinc-900 disabled:opacity-30"
                data-testid="page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 tabular-nums" data-testid="page-indicator">{page} / {pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages || loading}
                className="p-1.5 rounded border border-zinc-800 hover:bg-zinc-900 disabled:opacity-30"
                data-testid="page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Row({ row }) {
  const when = useMemo(() => {
    try {
      const d = new Date(row.received_at);
      return d.toLocaleString();
    } catch { return row.received_at; }
  }, [row.received_at]);

  const flag = (row.country_code || '').toUpperCase();
  const DeviceIcon = row.device_type === 'Mobile' || row.device_type === 'Tablet' ? Smartphone : Monitor;

  return (
    <tr className="border-t border-zinc-800/70 hover:bg-zinc-900/50" data-testid="ip-log-row">
      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{when}</td>
      <td className="px-3 py-2 font-mono text-xs text-zinc-200">{row.ip || ','}</td>
      <td className="px-3 py-2 text-zinc-300">
        {flag && <span className="inline-block bg-zinc-800 px-1.5 py-0.5 rounded text-[10px] font-mono mr-1.5">{flag}</span>}
        {row.country || (row.geo_resolved ? ',' : 'resolving…')}{row.city ? ` · ${row.city}` : ''}
      </td>
      <td className="px-3 py-2 text-zinc-300 max-w-[260px] truncate" title={row.path}>{row.path || '/'}</td>
      <td className="px-3 py-2 text-zinc-300">{row.browser}{row.browser_version ? ` ${row.browser_version}` : ''}</td>
      <td className="px-3 py-2 text-zinc-400">{row.os}</td>
      <td className="px-3 py-2 text-zinc-400">
        <span className="inline-flex items-center gap-1"><DeviceIcon className="w-3.5 h-3.5" /> {row.device_type}</span>
      </td>
      <td className="px-3 py-2">
        {row.is_bot ? (
          <span className="inline-flex items-center gap-1 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-full px-2 py-0.5 text-[11px]">
            <Bot className="w-3 h-3" /> Bot
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[11px]">
            <UserIcon className="w-3 h-3" /> Human
          </span>
        )}
      </td>
    </tr>
  );
}
